import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import { Workflow } from 'modules/workflows/types';
import { WorkflowType } from 'modules/workflows/enums';
import { createWorkflowDraftMutation } from 'api/mutations/workflows';
import { getEditWorkflowDraftLink, getNewWorkflowDraftPayload } from '../utils';

type GraphQLCreateDraftVariables = {
  workflow: Partial<Workflow>;
};

type GraphQLCreateDraftMutationResponse = {
  createWorkflowDraft: {
    id: number;
    workflowType: string;
  };
};

export const ERROR_MESSAGE =
  'Something went wrong. Try saving the journey again, or contact our support team if the problem continues.';

export const useCreateNewWorkflowDraft = () => {
  const dispatch = useDispatch();

  const [createDraft] = useMutation<
    GraphQLCreateDraftMutationResponse,
    GraphQLCreateDraftVariables
  >(createWorkflowDraftMutation, {
    onError: () => dispatch(addNegativeToast(ERROR_MESSAGE)),
  });

  return async ({
    name,
    onSuccess,
  }: {
    name: string;
    onSuccess: (workflow: Partial<Workflow>) => void;
  }) => {
    const workflow = { ...getNewWorkflowDraftPayload(name), id: null };
    try {
      const { data, errors } = await createDraft({
        variables: { workflow: { json: workflow } } as GraphQLCreateDraftVariables,
      });
      const id = data?.createWorkflowDraft?.id;
      const workflowType = data?.createWorkflowDraft?.workflowType as WorkflowType;
      if (!id || errors?.length) {
        dispatch(addNegativeToast(ERROR_MESSAGE));
        return;
      }
      onSuccess({ id, name, workflowType });

      dispatch(
        addPositiveToast(`"${name}" created.`, {
          actions: [
            { title: 'Open in new tab', href: getEditWorkflowDraftLink(id), externalAction: true },
          ],
        }),
      );
    } catch (error) {
      dispatch(addNegativeToast(ERROR_MESSAGE));
    }
  };
};
